import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  makeStyles,
  mergeClasses,
  Spinner,
  tokens,
  Text,
} from "@fluentui/react-components";
import React, { useState } from "react";
import { Grid } from "../common/Grid";
import { UseFormReturn } from "react-hook-form";
import { ISignForm } from "../../interfaces/ISignForm";
import { SignSummary } from "../sign-generator/SignSummary";
import { SignAdditionsForm } from "../form/SignAdditionsForm";
import { ContactInformationForm } from "../form/ContactInformationForm";
import { useTranslation } from "react-i18next";
import { IOrderStatus } from "../../interfaces/IOrderStatus";
import { OrderConfirmation } from "../order/OrderConfirmation";
import { GridItem } from "../common/GridItem";
import { ISignGenerate } from "../../interfaces/ISignGenerate";
import { ModalButtons } from "./ModalButtons";
import { ArrowDownloadFilled, DismissFilled } from "@fluentui/react-icons";
import { getImageFromElement } from "../../helper/useImageFromElement";
import { downloadPDF } from "../../helper/pdfHelper";

type CheckoutModalProps = {
  signForm: UseFormReturn<ISignForm, any, ISignForm>;
  orderStatus: IOrderStatus;
  signRef: React.MutableRefObject<HTMLDivElement>;
  logoRef: React.MutableRefObject<HTMLDivElement>;
  checkoutModalOpen: boolean;
  loading: boolean;
  setCheckoutModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setOrderStatus: React.Dispatch<React.SetStateAction<IOrderStatus>>;
  setGenerateSign: React.Dispatch<React.SetStateAction<ISignGenerate>>;
  onSubmit: (e?: React.BaseSyntheticEvent) => Promise<void>;
};

const useStyles = makeStyles({
  dialogSurface: {
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    maxWidth: "40vw",

    "@media (max-width: 2100px)": {
      maxWidth: "60vw",
    },
    "@media (max-width: 1700px)": {
      maxWidth: "80vw",
    },
    "@media (max-width: 1000px)": {
      maxWidth: "95vw",
    },
  },
  errorOrSuccess: {
    maxWidth: "30vw",
  },
  container: {
    margin: tokens.spacingVerticalS,
  },
  title: {
    fontSize: tokens.fontSizeHero700,
  },
  cursor: {
    cursor: "pointer",
  },
  spaceBetween: {
    display: "flex",
    justifyContent: "space-between",
    "> button > svg": {
      marginRight: "8px",
    },
  },
});

export const CheckoutModal = ({
  signForm,
  orderStatus,
  signRef,
  logoRef,
  checkoutModalOpen,
  loading,
  setCheckoutModalOpen,
  setOrderStatus,
  setGenerateSign,
  onSubmit,
}: CheckoutModalProps) => {
  const styles = useStyles();
  const { t } = useTranslation();
  const [step, setStep] = useState<number>(1);
  const { watch } = signForm;
  const order = watch();

  const generatePdf = async () => {
    const signImage = await getImageFromElement(signRef.current!);
    const logo = await getImageFromElement(logoRef.current!);
    await downloadPDF(order, signImage, logo);
  };

  return (
    <Dialog open={checkoutModalOpen}>
      <DialogSurface
        className={mergeClasses(
          styles.dialogSurface,
          (orderStatus.error || orderStatus?.orderSent) && styles.errorOrSuccess
        )}
      >
        <DialogBody
          style={{ display: "flex", flexDirection: "column", flex: 1 }}
        >
          <div className={styles.spaceBetween}>
            <DialogTitle
              className={orderStatus?.orderSent && styles.title}
              action={
                !orderStatus?.orderSent && !orderStatus?.error && !loading ? (
                  <DismissFilled
                    fontSize={16}
                    className={styles.cursor}
                    onClick={() => {
                      setCheckoutModalOpen(false);
                      setStep(1);
                    }}
                  />
                ) : undefined
              }
            >
              {t(
                loading
                  ? "label.loading"
                  : orderStatus?.orderSent
                  ? "label.orderSent"
                  : orderStatus?.error
                  ? "label.orderError"
                  : "label.confirmOrderAdditions"
              )}
            </DialogTitle>
            {orderStatus?.orderSent && (
              <Button
                appearance="secondary"
                size="large"
                onClick={() => {
                  if (orderStatus.orderSent) {
                    generatePdf();
                  }
                }}
              >
                <ArrowDownloadFilled />
                <Text>{t("button.downloadOrderConfirmation")}</Text>
              </Button>
            )}
          </div>

          <DialogContent style={{ overflowY: "auto" }}>
            <Grid className={styles.container}>
              {orderStatus?.orderSent || orderStatus.error || loading ? (
                <GridItem size="12/12">
                  {loading ? (
                    <Spinner />
                  ) : (
                    <OrderConfirmation
                      orderStatus={orderStatus}
                      order={order}
                      signRef={signRef}
                      logoRef={logoRef}
                    />
                  )}
                </GridItem>
              ) : (
                <>
                  <SignSummary signForm={signForm} step={step} />
                  {step === 1 ? (
                    <SignAdditionsForm signForm={signForm} />
                  ) : (
                    <ContactInformationForm signForm={signForm} />
                  )}
                </>
              )}
            </Grid>
          </DialogContent>

          <ModalButtons
            signForm={signForm}
            orderStatus={orderStatus}
            step={step}
            loading={loading}
            setOrderStatus={setOrderStatus}
            setGenerateSign={setGenerateSign}
            setCheckoutModalOpen={setCheckoutModalOpen}
            setStep={setStep}
            onSubmit={onSubmit}
          />
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};
